import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import startCase from "lodash/startCase";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import SizeIcon from "@mui/icons-material/FormatSize";
import ListItemText from "@mui/material/ListItemText";
import MoreIcon from "@mui/icons-material/MoreVert";
import CollectionsIcon from "@mui/icons-material/Collections";
import Button from "@mui/material/Button";
import FilterListIcon from "@mui/icons-material/FilterList";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Description as ExcelIcon,
  Publish as ImportAssetsIcon,
  Timeline as TimelineIcon,
  FindReplace as FindReplaceIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Visibility as ViewIcon,
  Build as ToolsIcon,
} from "@mui/icons-material";
import InventoryFiltersModal from "../InventoryFiltersModal";
import InfoDialog from "../../views/favoriteReport/fields/InfoDialog";

import useMutation from "../../hooks/useMutation";
import { useProjectInventory } from "../../contexts/projectInventory";
import { usePermissions } from "../../contexts/permissions";
import { useProject } from "../../contexts/project";
import useQueryParams from "../../hooks/useQueryParams";
import useRoles from "../../hooks/user/useRoles";
import ImportAssetsButton from "./ImportAssetsButton";
import LookupScanner from "../../components/barcode/LookupScanner";
import MenuActions from "../../components/MenuActions";
import FindReplaceWizard from "./FindReplaceWizard";
import {
  DOWNLOAD_ASSETS_IMAGES_MUTATION,
  EXPORT_EXCEL_DATA_MUTATION,
  EXPORT_ALT_EXCEL_DATA_MUTATION,
  EXPORT_LIFE_SCIENCES_EXCEL_DATA_MUTATION,
} from "../../constants/graphql";
import { useBanner } from "../../contexts/banner";
import ProjectImageModal from "../ProjectImageModal";
import SelectProjectAttentionModal from "../../components/capture/SelectProjectAttentionModal";
import ProjectInCartQuantityModal from "./ProjectInCartQuantityModal";

const SIZE_OPTIONS = ["small", "medium", "large", "x-large"];
const DENSITY_LABELS = {
  small: "Compact",
  medium: "Comfortable",
  large: "Large",
  "x-large": "Extra Large",
};

const MoreLabel = () => (
  <>
    <MoreIcon fontSize="small" color="action" />
    <Box mr={0.5} />
    <Typography color="textSecondary" variant="body2">
      More
    </Typography>
  </>
);

const FileUploadActionButton = ({ disabled, ...props }) => (
  <Button
    {...props}
    disabled={disabled}
    component="span"
    size="medium"
    variant="contained"
    color="primary"
  >
    {props.children}
  </Button>
);

export const SizeLabel = ({ value, variant = "body1", color }) => (
  <>
    <SizeIcon fontSize="small" color="action" />
    <Box mr={1} />
    <Typography color={color} variant={variant}>
      {startCase(value)}
    </Typography>
  </>
);

// Hook: detect mobile viewport
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);

  return isMobile;
};

// Nested Menu Item — controlled via shared activeSubmenu / setActiveSubmenu
const NestedMenuItem = ({ label, icon, children, popupId, activeSubmenu, setActiveSubmenu }) => {
  const isMobile = useIsMobile();
  const [openToLeft, setOpenToLeft] = useState(false);
  const menuItemRef = useRef(null);
  const closeTimeout = useRef(null);
  const isOpen = activeSubmenu === popupId;

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  const calcDirection = useCallback(() => {
    if (!menuItemRef.current) return;
    const { right } = menuItemRef.current.getBoundingClientRect();
    setOpenToLeft(window.innerWidth - right < 250);
  }, []);

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    calcDirection();
    setActiveSubmenu(popupId);
  }, [calcDirection, popupId, setActiveSubmenu]);

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setActiveSubmenu(prev => (prev === popupId ? null : prev));
    }, 200);
  }, [popupId, setActiveSubmenu]);

  const cancelClose = useCallback(() => clearTimeout(closeTimeout.current), []);

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        onMouseEnter={!isMobile ? open : undefined}
        onMouseLeave={!isMobile ? scheduleClose : undefined}
        onClick={isMobile ? open : undefined}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          {icon && <Box mr={1}>{icon}</Box>}
          <ListItemText primary={label} />
        </Box>
        <ChevronRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        open={isOpen}
        anchorEl={menuItemRef.current}
        onClose={() => setActiveSubmenu(null)}
        style={{ pointerEvents: "none" }}
        MenuListProps={{
          style: { pointerEvents: "auto" },
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
        }}
        anchorOrigin={{ vertical: "top", horizontal: openToLeft ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: openToLeft ? "right" : "left" }}
      >
        {children}
      </Menu>
    </>
  );
};

// Density Nested Menu — standalone (only nested item inside View submenu)
const DensityNestedMenuItem = ({ size, onChange, sizeOptions, densityLabels }) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [openToLeft, setOpenToLeft] = useState(false);
  const menuItemRef = useRef(null);
  const closeTimeout = useRef(null);

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  const calcDirection = useCallback(() => {
    if (!menuItemRef.current) return;
    const { right } = menuItemRef.current.getBoundingClientRect();
    setOpenToLeft(window.innerWidth - right < 250);
  }, []);

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    calcDirection();
    setIsOpen(true);
  }, [calcDirection]);

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  const cancelClose = useCallback(() => clearTimeout(closeTimeout.current), []);

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        onMouseEnter={!isMobile ? open : undefined}
        onMouseLeave={!isMobile ? scheduleClose : undefined}
        onClick={isMobile ? open : undefined}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center">
          <Box mr={1}>
            <SizeIcon fontSize="small" />
          </Box>
          <ListItemText primary="Density" />
        </Box>
        <ChevronRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        open={isOpen}
        anchorEl={menuItemRef.current}
        onClose={() => setIsOpen(false)}
        style={{ pointerEvents: "none" }}
        MenuListProps={{
          style: { pointerEvents: "auto" },
          onMouseEnter: cancelClose,
          onMouseLeave: scheduleClose,
        }}
        anchorOrigin={{ vertical: "top", horizontal: openToLeft ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: openToLeft ? "right" : "left" }}
      >
        {sizeOptions.map(option => (
          <MenuItem
            key={option}
            data-id="select-density"
            selected={size === option}
            onClick={() => {
              onChange({ size: option });
              setIsOpen(false);
            }}
          >
            <ListItemText primary={densityLabels[option] || startCase(option)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default function AssetsTableActions({
  actions,
  size,
  enableSize,
  onChange,
  fields,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  const { projectId, clientId } = useProjectInventory();
  const currentProject = useProject();
  const [exportExcelData] = useMutation(EXPORT_EXCEL_DATA_MUTATION);
  const [exportAltExcelData] = useMutation(EXPORT_ALT_EXCEL_DATA_MUTATION);
  const [exportLifeSciencesExcelData] = useMutation(EXPORT_LIFE_SCIENCES_EXCEL_DATA_MUTATION);
  const [downloadAssetsImages] = useMutation(DOWNLOAD_ASSETS_IMAGES_MUTATION);
  const { displayBanner } = useBanner();
  const [openGalleryModal, setOpenGalleryModal] = useState(false);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [isAttentionOpen, setIsAttentionOpen] = useState(false);
  const isFindReplaceOpen = params.has("findReplace");
  const [isImportAssetsOpen, setIsImportAssetsOpen] = useState(false);
  const { can, user } = usePermissions();
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const canViewShoppingCarts = useRoles(["admin", "manager", "client"]);
  const isProjectInCartModalOpen = canViewShoppingCarts && params.has("shoppingCarts");

  const isReceivingWizardDisabled = params.get("disable_receiving_wizard") === "true";

  const [lookupScanner, setLookupScanner] = useState(false);
  const toggleLookupScanner = () => setLookupScanner(v => !v);

  const openFindReplace = () => {
    if (requiresProject()) return;
    const newParams = new URLSearchParams(location.search);
    newParams.set("findReplace", "0");
    navigate({ search: newParams.toString() });
  };

  const openProjectInCartModal = () => {
    if (requiresProject()) return;
    const newParams = new URLSearchParams(location.search);
    newParams.set("shoppingCarts", "1");
    navigate({ search: newParams.toString() });
  };

  const closeProjectInCartModal = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete("shoppingCarts");
    navigate({ search: newParams.toString() });
  };

  const requiresProject = () => {
    if (projectId) return false;

    setIsAttentionOpen(true);
    return true;
  };

  const exportExcel = () => {
    // Warehouse-dropdown project routes deliberately use "null" for clientId.
    // A selected project is sufficient to scope the export in that case.
    if (!projectId && (!clientId || clientId === "null")) {
      setIsAttentionOpen(true);
      return;
    }

    exportExcelData({
      variables: {
        ...(projectId && { projectId }),
        clientId,
      },
    });
    displayBanner({
      message: "Generating file...",
      type: "export-excel",
      projectId,
      clientId,
      startedAt: Date.now(),
    });
  };

  const exportAltExcel = () => {
    if (requiresProject()) return;

    exportAltExcelData({
      variables: { projectId },
    });
    displayBanner({
      message: "Generating file...",
      type: "export-inventory-report",
      projectId,
      startedAt: Date.now(),
    });
  };

  const exportLifeSciencesExcel = () => {
    exportLifeSciencesExcelData({
      variables: {
        ...(projectId && { projectId }),
        clientId,
      },
    });
    displayBanner({
      message: "Generating file...",
      type: "export-life-sciences-excel",
      projectId,
      clientId,
      startedAt: Date.now(),
    });
  };

  const downloadImages = () => {
    if (requiresProject()) return;

    downloadAssetsImages({ variables: { projectId } });
    displayBanner({
      message: "Generating file...",
      type: "download_images",
      projectId,
      startedAt: Date.now(),
    });
  };

  const openGallery = () => {
    if (requiresProject()) return;

    setOpenGalleryModal(true);
  };

  const closeGallery = () => {
    setOpenGalleryModal(false);
  };

  const goToActivity = () => {
    navigate("/activity");
  };

  const handleImportAssets = () => {
    if (projectId) {
      setIsImportAssetsOpen(true);
    } else {
      setIsAttentionOpen(true);
    }
  };

  const toggleReceivingWizardDisabled = () => {
    const newParams = new URLSearchParams(window.location.search);
    if (isReceivingWizardDisabled) {
      newParams.delete("disable_receiving_wizard");
    } else {
      newParams.set("disable_receiving_wizard", "true");
    }
    navigate({ search: newParams.toString() });
  };

  const hasExportOptions =
    can("features.export_assets") &&
    (true || user.company.settings.receiving_wizard || user.company.settings.medical_fields);

  const hasImportOptions = can("features.import_assets");

  const [activeSubmenu, setActiveSubmenu] = useState(null);

  return (
    <>
      <MenuActions
        id="moreActionsButton"
        style={{ textTransform: "none" }}
        size="small"
        label={<MoreLabel />}
      >
        {/* Export Submenu */}
        {hasExportOptions && (
          <NestedMenuItem
            label="Export"
            icon={<ExportIcon />}
            popupId="export-submenu"
            activeSubmenu={activeSubmenu}
            setActiveSubmenu={setActiveSubmenu}
          >
            {can("features.export_assets") && (
              <MenuItem data-id="exportAssetsExcel" onClick={exportExcel}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Excel
              </MenuItem>
            )}
            {can("features.export_assets") && user.company.settings.display_inventory_report && (
              <MenuItem data-id="exportInventoryReport" onClick={exportAltExcel}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Inventory Report
              </MenuItem>
            )}
            {can("features.export_assets") && user.company.settings.medical_fields && (
              <MenuItem data-id="exportLifeSciencesExcel" onClick={exportLifeSciencesExcel}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Life Sciences Excel
              </MenuItem>
            )}
            {can("features.export_assets") && (
              <MenuItem data-id="downloadAssetsImages" onClick={downloadImages}>
                <Box mr={1}>
                  <ExcelIcon fontSize="small" />
                </Box>
                Download Images
              </MenuItem>
            )}
          </NestedMenuItem>
        )}

        {/* Import Submenu */}
        {hasImportOptions && (
          <NestedMenuItem
            label="Import"
            icon={<ImportIcon />}
            popupId="import-submenu"
            activeSubmenu={activeSubmenu}
            setActiveSubmenu={setActiveSubmenu}
          >
            <MenuItem data-id="importAssets" onClick={handleImportAssets}>
              <Box mr={1}>
                <ImportAssetsIcon fontSize="small" />
              </Box>
              Import Assets
            </MenuItem>
          </NestedMenuItem>
        )}

        {/* View Submenu */}
        <NestedMenuItem
          label="View"
          icon={<ViewIcon />}
          popupId="view-submenu"
          activeSubmenu={activeSubmenu}
          setActiveSubmenu={setActiveSubmenu}
        >
          <MenuItem data-id="projectGallery" onClick={openGallery}>
            <Box mr={1}>
              <CollectionsIcon fontSize="small" />
            </Box>
            Image Gallery
          </MenuItem>
          {isAdminOrManager && (
            <MenuItem data-id="replayLogHub" onClick={goToActivity}>
              <Box mr={1}>
                <TimelineIcon fontSize="small" />
              </Box>
              Replay Log Hub
            </MenuItem>
          )}
          {enableSize && (
            <DensityNestedMenuItem
              size={size}
              onChange={onChange}
              sizeOptions={SIZE_OPTIONS}
              densityLabels={DENSITY_LABELS}
            />
          )}
        </NestedMenuItem>

        {/* Tools Submenu */}
        <NestedMenuItem
          label="Tools"
          icon={<ToolsIcon />}
          popupId="tools-submenu"
          activeSubmenu={activeSubmenu}
          setActiveSubmenu={setActiveSubmenu}
        >
          <MenuItem data-id="findReplace" onClick={openFindReplace}>
            <Box mr={1}>
              <FindReplaceIcon fontSize="small" />
            </Box>
            Find & Replace
          </MenuItem>
          {canViewShoppingCarts && (
            <MenuItem data-id="projectInCartQuantity" onClick={openProjectInCartModal}>
              <Box mr={1}>
                <ViewIcon fontSize="small" />
              </Box>
              View Shopping Carts
            </MenuItem>
          )}
        </NestedMenuItem>

        {/* Filters Submenu */}
        {user.company.settings.receiving_wizard && (
          <NestedMenuItem
            label="Filters"
            icon={<FilterListIcon />}
            popupId="filters-submenu"
            activeSubmenu={activeSubmenu}
            setActiveSubmenu={setActiveSubmenu}
          >
            <MenuItem data-id="inventoryFilters" onClick={() => setOpenFiltersModal(true)}>
              <Box mr={1}>
                <FilterListIcon fontSize="small" />
              </Box>
              Inventory Filters
            </MenuItem>
          </NestedMenuItem>
        )}

        {/* Receiving Assistant Toggle (if applicable) */}
        {currentProject?.settings?.onsite_capture_to_warehouse_storage && (
          <MenuItem data-id="toggleReceivingWizard" onClick={toggleReceivingWizardDisabled}>
            <Box mr={1}>
              {isReceivingWizardDisabled ? <ToggleOnIcon color="action" /> : <ToggleOffIcon />}
            </Box>
            {isReceivingWizardDisabled
              ? "Enable Receiving Assistant"
              : "Temporarily Disable Receiving Assistant"}
          </MenuItem>
        )}

        {actions}
      </MenuActions>
      <SelectProjectAttentionModal
        open={isAttentionOpen}
        onClose={() => setIsAttentionOpen(false)}
      />
      {lookupScanner && (
        <LookupScanner projectId={projectId} clientId={clientId} onClose={toggleLookupScanner} />
      )}
      <ProjectImageModal open={openGalleryModal} onClose={closeGallery} />
      {isFindReplaceOpen && (
        <FindReplaceWizard
          open={isFindReplaceOpen}
          fields={fields}
        />
      )}
      {openFiltersModal && <InventoryFiltersModal onClose={() => setOpenFiltersModal(false)} />}
      {isProjectInCartModalOpen && (
        <ProjectInCartQuantityModal
          projectId={projectId}
          onClose={closeProjectInCartModal}
        />
      )}
      <InfoDialog
        open={isImportAssetsOpen}
        onClose={() => setIsImportAssetsOpen(false)}
        title="Import Assets"
        description="Use the template below to import assets:"
        downloadUrl="https://cdn.filestackcontent.com/DVmhiqb0TnzfO4H7i0gA"
        downloadText="Download Template"
        actions={
          <ImportAssetsButton
            keepOpen
            component={FileUploadActionButton}
            label="Import"
            setIsAttentionOpen={setIsAttentionOpen}
            projectId={projectId}
            assetType={currentProject?.settings?.onsite_manifest ? 'Sku' : undefined}
            color="secondary"
            variant="outlined"
            size="small"
          />
        }
      />
    </>
  );
}
